/**
 * <RoomChangeModal> — the ONE room-change UI for the whole app (Front Desk workspace + PMS Stay detail).
 * Visual floor picker + reason codes → POST /fo/stays/{stayId}/change-room { new_room_id, reason_code, note }.
 * Always sends the room's id (never room_number) and the stay's id (never a guest/arrival id).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { OperationalDrawer, BottomActionBar, FloorRoomPicker } from "@/components/operational";
import { ArrowRightLeft } from "lucide-react";
import { foApi, formatApiError } from "@/pages/fo/foApi";

export default function RoomChangeModal({ open, onOpenChange, stayId, hotelId, currentRoomId, currentRoomNumber, stayCode, onSuccess }) {
  const [rooms, setRooms] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reasonCode, setReasonCode] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([foApi.rooms(hotelId ? { hotel_id: hotelId } : {}), foApi.roomChangeReasons()])
      .then(([r, rs]) => {
        setRooms((r.items || []).filter(x => x.id !== currentRoomId));
        setReasons(rs.items || []);
        setReasonCode((rs.items || [])[0]?.code || "");
      })
      .catch((e) => toast.error(formatApiError(e, "Could not load rooms")))
      .finally(() => setLoading(false));
    setSelected(null);
    setNote("");
  }, [open, hotelId, currentRoomId]);

  const submit = async () => {
    if (!stayId) { toast.error("Stay not loaded yet"); return; }
    if (!selected) { toast.error("Select a target room"); return; }
    if (!reasonCode) { toast.error("Pick a reason"); return; }
    setSaving(true);
    try {
      await foApi.changeRoom(stayId, { new_room_id: selected.id, reason_code: reasonCode, note });
      onSuccess?.(selected);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(formatApiError(e, "Room change failed"));
    } finally { setSaving(false); }
  };

  const currentLabel = currentRoomNumber || rooms.find(r => r.id === currentRoomId)?.number || "?";

  return (
    <OperationalDrawer open={open} onOpenChange={onOpenChange}
      title="Room Change" subtitle={`${stayCode || ""}${stayCode ? " · " : ""}Room ${currentLabel}`}
      size="lg" testid="room-change-drawer"
      footer={
        <BottomActionBar
          primary={{ label: saving ? "Moving..." : "Confirm Room Change", icon: ArrowRightLeft, tone: "primary", onClick: submit,
                     loading: saving, disabled: !selected || !reasonCode, testid: "roomchange-confirm" }}
          secondaryActions={[{ label: "Cancel", tone: "secondary", onClick: () => onOpenChange(false), testid: "roomchange-cancel" }]}
          helper={selected ? `Room ${currentLabel} → Room ${selected.number}` : "Pick a target room"}
        />
      }>
      <div className="p-4 space-y-4">
        <div>
          <div className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Reason</div>
          <div className="flex flex-wrap gap-2" data-testid="roomchange-reasons">
            {reasons.map(r => (
              <button key={r.code} type="button" data-testid={`roomchange-reason-${r.code}`} onClick={() => setReasonCode(r.code)}
                className={["min-h-[44px] px-3 py-1.5 rounded-lg border-2 text-[13px] font-medium",
                  reasonCode === r.code ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint" : "bg-white border-hocp-border hover:border-hocp-mint"].join(" ")}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <FloorRoomPicker rooms={rooms} loading={loading} onSelect={setSelected} selectedRoomId={selected?.id} testid="roomchange-picker" />
        <div>
          <label className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold">Note</label>
          <textarea value={note} onChange={e => setNote(e.target.value)}
            className="w-full mt-1 border-2 border-hocp-border rounded-lg p-2 text-[14px] min-h-[60px] bg-white" data-testid="roomchange-note" />
        </div>
      </div>
    </OperationalDrawer>
  );
}
